import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lock, ArrowRight, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";

export default function AdminLogin() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { document.title = "Admin — Ana Paula Moraes"; }, []);
  useEffect(() => {
    if (user?.role === "admin") navigate("/admin", { replace: true });
    else if (user) navigate("/membros", { replace: true });
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const u = await login(email, password);
      if (u.role !== "admin") {
        setError("Esta conta não tem privilégios de administrador.");
        return;
      }
      toast.success("Bem-vinda, Ana Paula!");
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-10 sm:px-6 sm:py-12 relative" style={{ background: "var(--bg)" }}>
      {/* Decorative blob */}
      <div className="absolute inset-0 brand-blob -z-10" />

      <div className="max-w-md w-full brand-card-flat p-7 sm:p-10 fade-in-up shadow-xl" style={{ border: "1px solid var(--border-light)" }}>
        <div className="flex items-center gap-3 mb-7">
          <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "rgba(196,143,149,0.12)" }}>
            <Lock size={16} style={{ color: "var(--copper)" }} />
          </div>
          <Link to="/" className="brand-logo text-lg sm:text-xl">
            <span className="ana">Ana Paula</span> <span className="m">Moraes</span>
          </Link>
        </div>

        <div className="flex items-center gap-2 mb-3">
          <span className="dot-ornament" />
          <span className="eyebrow">Acesso restrito</span>
        </div>
        <h1 className="font-serif text-2xl sm:text-3xl mb-2 leading-tight" style={{ color: "var(--text-primary)" }}>
          Painel <span className="headline-italic-accent">administrativo</span>
        </h1>
        <p className="text-sm mb-7 leading-relaxed" style={{ color: "var(--text-secondary)" }}>
          Entre com suas credenciais de administrador.
        </p>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.28em] font-semibold mb-2 block" style={{ color: "var(--copper)" }}>E-mail</label>
            <input type="email" required autoComplete="email" inputMode="email" className="brand-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@anapaulam.com" data-testid="admin-login-email" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.28em] font-semibold mb-2 block" style={{ color: "var(--copper)" }}>Senha</label>
            <div className="relative">
              <input
                type={showPw ? "text" : "password"}
                required
                autoComplete="current-password"
                className="brand-input pr-12"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="admin-login-password"
              />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-lg hover:bg-[color:var(--bg-warm)] transition-colors" aria-label="Mostrar senha" style={{ color: "var(--text-muted)" }}>
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          {error && (
            <div className="text-sm py-3 px-4 rounded-xl" style={{ background: "rgba(220,38,38,0.06)", color: "#b91c1c", border: "1px solid rgba(220,38,38,0.15)" }} data-testid="admin-login-error">
              {error}
            </div>
          )}
          <button type="submit" disabled={submitting} className="btn-primary w-full mt-2" data-testid="admin-login-submit">
            {submitting ? "Entrando…" : (<>Acessar painel <ArrowRight size={16} /></>)}
          </button>
        </form>

        <div className="mt-8 pt-5 text-center text-xs" style={{ borderTop: "1px solid var(--border-light)", color: "var(--text-muted)" }}>
          <Link to="/login" className="hover:underline">Acesso de aluna</Link>
        </div>
      </div>
    </div>
  );
}
